import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Mail, Phone, MapPin, ArrowRight, Loader2, MessageSquare, Clock, Globe } from 'lucide-react';

const ContactPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      alert("Message sent! We'll be in touch shortly.");
      setFormData({ firstName: '', lastName: '', email: '', message: '' });
    }, 1500);
  };

  const contactMethods = [
    {
      icon: <Mail className="w-5 h-5 text-blue-600" />,
      title: "Email",
      value: "sales@asha.health",
      link: "mailto:sales@asha.health",
      description: "For general inquiries and sales"
    },
    {
      icon: <Phone className="w-5 h-5 text-blue-600" />,
      title: "Phone",
      value: "(914) 677-9846",
      link: "tel:+19146779846",
      description: "Mon-Fri from 9am to 6pm EST"
    },
    {
      icon: <MapPin className="w-5 h-5 text-blue-600" />,
      title: "Office",
      value: "Manhattan, New York 10065",
      description: "Come say hello at our HQ"
    }
  ];

  return (
    <div className="min-h-screen bg-white selection:bg-blue-100 selection:text-blue-900">
      <Header />
      
      <style>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>

      <main className="pt-24 pb-12 px-4 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-blue-50 via-white to-indigo-50/50"></div>
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-100/40 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob"></div>
          <div className="absolute top-0 -left-4 w-[500px] h-[500px] bg-purple-100/40 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-[500px] h-[500px] bg-indigo-100/40 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-4000"></div>
        </div>

        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-20 items-start justify-center">
            
            {/* Left Column: Contact Info & Value Prop */}
            <div className="flex-1 lg:pt-8">
              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight text-slate-900 tracking-tight">
                Get in <br/>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                  Touch
                </span>
              </h1>
              
              <p className="text-lg text-slate-600 mb-10 leading-relaxed max-w-lg">
                Have questions about how Asha Dental can transform your practice? We're here to help you automate operations and grow revenue.
              </p>

              <div className="space-y-6 mb-12">
                {contactMethods.map((method, index) => (
                  <div key={index} className="flex gap-4 p-4 rounded-xl bg-white/60 border border-white/60 shadow-sm hover:shadow-md transition-all hover:bg-white/80">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center border border-blue-100">
                      {method.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 text-sm mb-1">{method.title}</h3>
                      {method.link ? (
                        <a href={method.link} className="text-base font-medium text-blue-600 hover:text-blue-700 transition-colors block mb-1">
                          {method.value}
                        </a>
                      ) : (
                        <p className="text-base font-medium text-slate-800 mb-1">{method.value}</p>
                      )}
                      <p className="text-xs text-slate-500">{method.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-blue-50/50 rounded-2xl p-6 border border-blue-100">
                <h3 className="font-semibold text-slate-900 mb-2">Looking for a demo?</h3>
                <p className="text-slate-600 text-sm mb-4">
                  Schedule a personalized walkthrough to see our AI agents in action.
                </p>
                <Button 
                  onClick={() => window.location.href='/book-demo'}
                  variant="outline"
                  className="bg-white hover:bg-slate-50 text-blue-600 border-blue-200"
                >
                  Book a Demo
                </Button>
              </div>
            </div>

            {/* Right Column: Contact Form */}
            <div className="w-full lg:w-[480px] flex-shrink-0 relative z-10">
              <Card className="shadow-2xl border-0 ring-1 ring-slate-900/5 bg-white/90 backdrop-blur-sm overflow-hidden">
                <div className="h-1.5 bg-gradient-to-r from-blue-500 via-indigo-500 to-blue-600"></div>
                <CardHeader className="pb-4 pt-6 px-6">
                  <CardTitle className="text-xl font-bold text-slate-900">
                    Send us a message
                  </CardTitle>
                  <CardDescription className="text-slate-500 text-sm mt-1">
                    Fill out the form below and we'll get back to you shortly.
                  </CardDescription>
                </CardHeader>
                <CardContent className="px-6 pb-6">
                  <form className="space-y-4" onSubmit={handleSubmit}>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label htmlFor="firstName" className="text-xs font-semibold text-slate-700">First name</Label>
                        <Input 
                          id="firstName" 
                          placeholder="Jane" 
                          value={formData.firstName}
                          onChange={handleInputChange}
                          required
                          className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-10 text-sm" 
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="lastName" className="text-xs font-semibold text-slate-700">Last name</Label>
                        <Input 
                          id="lastName" 
                          placeholder="Doe"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          required
                          className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-10 text-sm" 
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-1.5">
                      <Label htmlFor="email" className="text-xs font-semibold text-slate-700">Work email</Label>
                      <Input 
                        id="email" 
                        type="email" 
                        placeholder="jane@practice.com"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-10 text-sm" 
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="message" className="text-xs font-semibold text-slate-700">Message</Label>
                      <Textarea 
                        id="message" 
                        placeholder="How can we help you?"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 min-h-[120px] resize-none transition-all text-sm"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      disabled={isSubmitting}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white h-11 text-base font-semibold rounded-lg shadow-lg shadow-blue-200 hover:shadow-blue-300 transition-all transform hover:-translate-y-0.5 mt-2"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Message
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ContactPage;